import { z } from "zod";
import { authoringCreatedBySchema, authoringSlugSchema, authoringTenantSlugSchema, } from "./authoring-shared.js";
import { canonicalTwinBoundaryJson } from "./twin-logic.js";
const boundedText = (maximum) => z.string().trim().min(1).max(maximum);
const identifierSchema = boundedText(200);
const organisationIdSchema = boundedText(200);
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/u);
const positiveTimeSchema = z.number().int().positive().safe();
export const twinAuthoringScopeSchema = z.object({
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
}).strict();
export const twinAuthoringDecisionOutcomeSchema = z.enum([
    "accepted",
    "rejected",
    "clarification_required",
]);
export const twinAuthoringRunStateSchema = z.enum([
    "queued",
    "running",
    "succeeded",
    "failed",
    "cancelled",
]);
const sourceRelationSchema = z.string().regex(/^[A-Za-z_][A-Za-z0-9_$]*\.[A-Za-z_][A-Za-z0-9_$]*$/u);
export const twinAuthoringSourceProjectionV1Schema = z.object({
    logicalSchema: z.string().regex(/^[A-Za-z_][A-Za-z0-9_$]*$/u),
    relations: z.array(z.discriminatedUnion("runtimeSchema", [
        z.object({
            logicalRelation: sourceRelationSchema,
            runtimeSchema: z.literal("connector_warehouse"),
            runtimeRelation: sourceRelationSchema,
            admittedDependencyRef: z.null(),
        }).strict(),
        z.object({
            logicalRelation: sourceRelationSchema,
            runtimeSchema: z.literal("mlx_curated"),
            runtimeRelation: sourceRelationSchema,
            admittedDependencyRef: identifierSchema,
        }).strict(),
    ])).min(1).max(256),
    projectionSha256: sha256Schema,
}).strict().superRefine((projection, context) => {
    const logical = projection.relations.map(({ logicalRelation }) => logicalRelation);
    const runtime = projection.relations.map(({ runtimeRelation }) => runtimeRelation);
    const sorted = [...projection.relations].sort((left, right) => left.logicalRelation < right.logicalRelation ? -1 : left.logicalRelation > right.logicalRelation ? 1 : 0);
    if (new Set(logical).size !== logical.length || new Set(runtime).size !== runtime.length
        || JSON.stringify(projection.relations) !== JSON.stringify(sorted)) {
        context.addIssue({ code: "custom", path: ["relations"],
            message: "Source projection relations must be one-to-one, unique, and sorted." });
    }
    if (projection.relations.some(({ logicalRelation, runtimeRelation, runtimeSchema }) => !logicalRelation.startsWith(`${projection.logicalSchema}.`)
        || !runtimeRelation.startsWith(`${runtimeSchema}.`))) {
        context.addIssue({ code: "custom", path: ["relations"],
            message: "Source projection relations must stay within their exact schemas." });
    }
});
export const twinAuthoringRunRequestV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-authoring.run-request.v1"),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    idempotencyKey: boundedText(200),
    requestedBy: authoringCreatedBySchema,
    sourceSnapshot: z.object({
        kind: z.literal("evidence_cut"),
        id: identifierSchema,
        manifestSha256: sha256Schema,
    }).strict(),
    sourceProjection: twinAuthoringSourceProjectionV1Schema,
    authoringPolicy: z.object({
        id: identifierSchema,
        version: boundedText(80),
        sha256: sha256Schema,
    }).strict(),
    productScope: z.discriminatedUnion("mode", [
        z.object({ mode: z.literal("all") }).strict(),
        z.object({
            mode: z.literal("selected"),
            productSlugs: z.array(authoringSlugSchema).min(1).max(128)
                .transform((slugs) => [...slugs].sort()),
        }).strict(),
    ]),
}).strict().superRefine((request, context) => {
    if (request.productScope.mode === "selected"
        && new Set(request.productScope.productSlugs).size
            !== request.productScope.productSlugs.length) {
        context.addIssue({
            code: "custom",
            path: ["productScope", "productSlugs"],
            message: "Selected product slugs must be unique.",
        });
    }
});
export const twinAuthoringRunRecordSchema = z.object({
    id: z.string().regex(/^ta_run_[0-9a-f]{32}$/u),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    requestSha256: sha256Schema,
    idempotencyKey: boundedText(200),
    state: twinAuthoringRunStateSchema,
    attemptCount: z.number().int().nonnegative().safe(),
    availableAt: positiveTimeSchema,
    leaseOwner: boundedText(200).nullable(),
    leaseExpiresAt: positiveTimeSchema.nullable(),
    lastHeartbeatAt: positiveTimeSchema.nullable(),
    startedAt: positiveTimeSchema.nullable(),
    finishedAt: positiveTimeSchema.nullable(),
    lastErrorCode: boundedText(120).nullable(),
    lastErrorMessage: boundedText(4_000).nullable(),
    createdAt: positiveTimeSchema,
    updatedAt: positiveTimeSchema,
}).strict().superRefine((run, context) => {
    const leased = run.leaseOwner !== null && run.leaseExpiresAt !== null;
    if ((run.state === "running") !== leased) {
        context.addIssue({ code: "custom", message: "Only running authoring runs hold a lease." });
    }
    const terminal = ["succeeded", "failed", "cancelled"].includes(run.state);
    if (terminal !== (run.finishedAt !== null)) {
        context.addIssue({ code: "custom", message: "Only terminal authoring runs are finished." });
    }
});
export const twinAuthoringEnqueueInputSchema = z.object({
    request: twinAuthoringRunRequestV1Schema,
    createdAt: positiveTimeSchema,
    availableAt: positiveTimeSchema.optional(),
}).strict();
export const twinAuthoringClaimInputSchema = twinAuthoringScopeSchema.extend({
    modelSelectionProtocolVersion: z.enum(["1", "2"]).optional(),
    workerId: boundedText(200),
    now: positiveTimeSchema,
    leaseMs: z.number().int().positive().max(5 * 60 * 1_000),
}).strict();
export const claimedTwinAuthoringRunSchema = z.object({
    run: twinAuthoringRunRecordSchema,
    request: twinAuthoringRunRequestV1Schema,
}).strict();
export const twinAuthoringHeartbeatInputSchema = twinAuthoringScopeSchema.extend({
    runId: twinAuthoringRunRecordSchema.shape.id,
    workerId: boundedText(200),
    attemptCount: z.number().int().positive().safe(),
    now: positiveTimeSchema,
    leaseMs: z.number().int().positive().max(5 * 60 * 1_000),
}).strict();
export const twinAuthoringFinishInputSchema = twinAuthoringScopeSchema.extend({
    runId: twinAuthoringRunRecordSchema.shape.id,
    workerId: boundedText(200),
    attemptCount: z.number().int().positive().safe(),
    now: positiveTimeSchema,
    result: z.discriminatedUnion("state", [
        z.object({ state: z.literal("succeeded") }).strict(),
        z.object({
            state: z.literal("failed"),
            errorCode: boundedText(120),
            errorMessage: boundedText(4_000),
        }).strict(),
    ]),
}).strict();
export const twinAuthoringRequeueInputSchema = twinAuthoringScopeSchema.extend({
    runId: twinAuthoringRunRecordSchema.shape.id,
    workerId: boundedText(200),
    attemptCount: z.number().int().positive().safe(),
    now: positiveTimeSchema,
    availableAt: positiveTimeSchema,
    errorCode: boundedText(120),
    errorMessage: boundedText(4_000),
}).strict().refine((input) => input.availableAt >= input.now, {
    path: ["availableAt"],
    message: "A requeued run cannot become available in the past.",
});
export const twinAuthoringEventKindSchema = z.enum([
    "run_queued",
    "run_claimed",
    "run_requeued",
    "run_cancelled",
    "run_succeeded",
    "run_failed",
    "stage_recorded",
    "artifact_stored",
    "decision_recorded",
    "release_recorded",
]);
const eventAttributeValueSchema = z.union([
    z.string().max(1_000),
    z.number().finite().safe(),
    z.boolean(),
    z.null(),
]);
const eventAttributesSchema = z.record(z.string().regex(/^[a-z][a-zA-Z0-9]{0,79}$/u), eventAttributeValueSchema).superRefine((attributes, context) => {
    if (Object.keys(attributes).length > 32) {
        context.addIssue({ code: "custom", message: "An event may contain at most 32 attributes." });
    }
});
export const twinAuthoringRunEventV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-authoring.run-event.v1"),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    runId: twinAuthoringRunRecordSchema.shape.id,
    kind: twinAuthoringEventKindSchema,
    actor: boundedText(200),
    attributes: eventAttributesSchema,
    createdAt: positiveTimeSchema,
}).strict();
export const twinAuthoringArtifactKindSchema = z.enum([
    "input_manifest",
    "source_snapshot",
    "discovery_profile",
    "model_invocation_receipt",
    "model_invocation_exchange",
    "author_pass_exchange",
    "author_pass_outcome",
    "semantic_authoring_record",
    "semantic_evaluation_record",
    "automated_decision_policy",
    "automated_decision_record",
    "automated_authoring_closure",
    "sqlmesh_plan",
    "validation_report",
    "bundle_manifest",
    "verified_authoring_release",
    "member_archive",
    "terminal_submission_envelope",
]);
const durableArtifactContracts = {
    input_manifest: {
        artifactType: "twin_authoring_input_manifest", artifactSchemaVersion: ["3.0.0", "4.0.0"],
    },
    source_snapshot: {
        artifactType: "twin_authoring_inspection", artifactSchemaVersion: "2.0.0",
    },
    discovery_profile: {
        artifactType: "twin_authoring_discovery", artifactSchemaVersion: "1.0.0",
    },
    model_invocation_receipt: {
        artifactType: "twin_authoring_model_invocation", artifactSchemaVersion: "2.0.0",
    },
    model_invocation_exchange: {
        artifactType: "twin_authoring_model_invocation_exchange", artifactSchemaVersion: "1.0.0",
    },
    author_pass_exchange: {
        artifactType: "twin_authoring_author_pass_exchange", artifactSchemaVersion: "1.0.0",
    },
    author_pass_outcome: {
        artifactType: "twin_authoring_author_pass_outcome", artifactSchemaVersion: "1.0.0",
    },
    semantic_authoring_record: {
        artifactType: "twin_authoring_semantic_record", artifactSchemaVersion: "1.0.0",
    },
    semantic_evaluation_record: {
        artifactType: "twin_authoring_semantic_evaluation", artifactSchemaVersion: "1.0.0",
    },
    automated_decision_policy: {
        artifactType: "twin_authoring_automated_decision_policy", artifactSchemaVersion: "1.0.0",
    },
    automated_decision_record: {
        artifactType: "twin_authoring_automated_decision", artifactSchemaVersion: "1.0.0",
    },
    automated_authoring_closure: {
        artifactType: "twin_authoring_automated_closure", artifactSchemaVersion: "1.0.0",
    },
    validation_report: {
        artifactType: "twin_authoring_validation_report", artifactSchemaVersion: "1.0.0",
    },
    bundle_manifest: {
        artifactType: "twin_authoring_bundle", artifactSchemaVersion: "3.0.0",
    },
    verified_authoring_release: {
        artifactType: "twin_authoring_verified_release", artifactSchemaVersion: "2.0.0",
    },
    member_archive: {
        artifactType: "twin_authoring_member_archive", artifactSchemaVersion: "1.0.0",
    },
    terminal_submission_envelope: {
        artifactType: "twin_authoring_terminal_submission", artifactSchemaVersion: "1.0.0",
    },
};
export const twinAuthoringArtifactDescriptorSchema = z.object({
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    kind: twinAuthoringArtifactKindSchema,
    artifactType: z.string().regex(/^[a-z][a-z0-9_]{0,119}$/u),
    artifactSchemaVersion: z.string().regex(/^[1-9][0-9]*\.[0-9]+\.[0-9]+$/u),
    contentType: z.string().regex(/^[a-z0-9][a-z0-9.+-]*\/[a-z0-9][a-z0-9.+-]*$/u),
    contentSha256: sha256Schema,
    byteSize: z.number().int().positive().max(16 * 1_024 * 1_024).safe(),
    rowCount: z.literal(0),
    storageLocator: z.string().trim().min(1).max(2_048)
        .regex(/^[a-z][a-z0-9+.-]*:\/\/[^\s?#]+$/u)
        .refine((value) => !/^[a-z][a-z0-9+.-]*:\/\/[^/]*@/u.test(value), "Artifact locators must not contain credentials."),
    producer: z.object({
        name: boundedText(120),
        version: boundedText(80),
        buildSha256: sha256Schema,
    }).strict(),
    createdBy: authoringCreatedBySchema,
    createdAt: positiveTimeSchema,
}).strict().superRefine((artifact, context) => {
    const expected = durableArtifactContracts[artifact.kind];
    const expectedVersions = typeof expected?.artifactSchemaVersion === "string"
        ? [expected.artifactSchemaVersion]
        : expected?.artifactSchemaVersion;
    if (expected && (artifact.artifactType !== expected.artifactType
        || !expectedVersions?.includes(artifact.artifactSchemaVersion))) {
        context.addIssue({
            code: "custom",
            path: ["artifactType"],
            message: `${artifact.kind} must use its exact durable artifact contract.`,
        });
    }
});
export const twinAuthoringArtifactRoleSchema = z.enum([
    "input",
    "discovery",
    "author_receipt",
    "author_exchange",
    "author_pass_exchange",
    "author_pass_outcome",
    "semantic_authoring",
    "validation",
    "bundle_manifest",
    "evaluator_receipt",
    "evaluator_exchange",
    "semantic_evaluation",
    "decision_policy",
    "decision",
    "closure",
    "release",
    "authored_members",
    "frozen_bundle",
    "emitted_release",
    "terminal_submission",
]);
const modelInvocationExchangeV1Schema = z.object({
    artifact_kind: z.literal("twin_authoring_model_invocation_exchange"),
    schema_version: z.literal("1.0.0"),
    role: z.enum(["semantic_author", "semantic_evaluator"]),
    request_body_base64: z.string().min(4).max(7_000_000),
    request_body_bytes: z.number().int().positive().max(5 * 1024 * 1024),
    request_body_sha256: sha256Schema,
    structured_output_base64: z.string().min(4).max(7_000_000),
    structured_output_bytes: z.number().int().positive().max(5 * 1024 * 1024),
    structured_output_sha256: sha256Schema,
    row_bearing_payloads: z.literal(0),
    exchange_sha256: sha256Schema,
}).strict();
const evaluatorFeedbackExchangeV1Schema = z.object({
    requested_model_id: boundedText(2_000),
    provider_reported_model_id: boundedText(2_000),
    provider_response_id: boundedText(2_000),
    provider_request_id: boundedText(2_000).nullable(),
    started_at: z.iso.datetime({ offset: true }),
    completed_at: z.iso.datetime({ offset: true }),
    input_tokens: z.number().int().nonnegative().safe().nullable(),
    output_tokens: z.number().int().nonnegative().safe().nullable(),
    total_tokens: z.number().int().nonnegative().safe().nullable(),
    exchange: modelInvocationExchangeV1Schema,
}).strict().superRefine((value, context) => {
    if (value.exchange.role !== "semantic_evaluator") {
        context.addIssue({ code: "custom", path: ["exchange", "role"],
            message: "Author repair feedback must contain a semantic evaluator exchange." });
    }
    if (value.requested_model_id !== value.provider_reported_model_id) {
        context.addIssue({ code: "custom", path: ["provider_reported_model_id"],
            message: "Evaluator feedback provider model differs from requested model." });
    }
    const usage = [value.input_tokens, value.output_tokens, value.total_tokens];
    if (usage.some((item) => item === null) && usage.some((item) => item !== null)) {
        context.addIssue({ code: "custom", path: ["total_tokens"],
            message: "Evaluator feedback token usage must be wholly present or absent." });
    }
    else if (value.total_tokens !== null
        && value.total_tokens !== (value.input_tokens ?? 0) + (value.output_tokens ?? 0)) {
        context.addIssue({ code: "custom", path: ["total_tokens"],
            message: "Evaluator feedback token usage is inconsistent." });
    }
});
export const twinAuthoringAuthorPassExchangeV1Schema = z.object({
    artifact_kind: z.literal("twin_authoring_author_pass_exchange"),
    schema_version: z.literal("1.0.0"),
    run_id: z.string().regex(/^ta_run_[0-9a-f]{32}$/u),
    attempt_count: z.number().int().positive().safe(),
    // Initial authoring, bounded objective repair, evidence expansion, and evaluator repair.
    pass_index: z.number().int().min(1).max(27),
    phase: z.enum(["initial", "refinement", "expansion", "evaluation_repair"]),
    phase_index: z.number().int().min(0).max(24),
    previous_pass_artifact_sha256: sha256Schema.nullable(),
    semantic_plan_sha256: sha256Schema,
    authored_member_ledger_sha256: sha256Schema.nullable(),
    requested_model_id: boundedText(2_000),
    provider_reported_model_id: boundedText(2_000),
    provider_response_id: boundedText(2_000),
    provider_request_id: boundedText(2_000).nullable(),
    started_at: z.iso.datetime({ offset: true }),
    completed_at: z.iso.datetime({ offset: true }),
    duration_ms: z.number().int().nonnegative().safe(),
    input_tokens: z.number().int().nonnegative().safe().nullable(),
    output_tokens: z.number().int().nonnegative().safe().nullable(),
    total_tokens: z.number().int().nonnegative().safe().nullable(),
    exchange: modelInvocationExchangeV1Schema,
    evaluator_feedback: evaluatorFeedbackExchangeV1Schema.nullable().optional(),
    row_bearing_payloads: z.literal(0),
}).strict().superRefine((value, context) => {
    if (value.phase === "initial" && (value.pass_index !== 1 || value.phase_index !== 0)) {
        context.addIssue({ code: "custom", path: ["phase"],
            message: "Initial author pass must be global pass 1 and phase index 0." });
    }
    if (value.phase !== "initial" && value.phase_index < 1) {
        context.addIssue({ code: "custom", path: ["phase_index"],
            message: "Refinement and expansion passes require a positive phase index." });
    }
    if (value.phase === "expansion" && value.phase_index !== 1) {
        context.addIssue({ code: "custom", path: ["phase_index"],
            message: "Authoring permits exactly one evidence expansion pass." });
    }
    if (value.phase === "evaluation_repair" && value.phase_index !== 1) {
        context.addIssue({ code: "custom", path: ["phase_index"],
            message: "Authoring permits exactly one evaluator-informed repair pass." });
    }
    if ((value.pass_index === 1) !== (value.previous_pass_artifact_sha256 === null)) {
        context.addIssue({ code: "custom", path: ["previous_pass_artifact_sha256"],
            message: "Author pass predecessor is inconsistent with its pass index." });
    }
    if (value.requested_model_id !== value.provider_reported_model_id) {
        context.addIssue({ code: "custom", path: ["provider_reported_model_id"],
            message: "Provider-reported model differs from requested model." });
    }
    if (value.exchange.role !== "semantic_author") {
        context.addIssue({ code: "custom", path: ["exchange", "role"],
            message: "Author pass exchange must contain a semantic author exchange." });
    }
    if ((value.phase === "evaluation_repair") !== (value.evaluator_feedback != null)) {
        context.addIssue({ code: "custom", path: ["evaluator_feedback"],
            message: "Evaluator feedback must be present exactly on the evaluator repair pass." });
    }
    const startedAt = Date.parse(value.started_at);
    const completedAt = Date.parse(value.completed_at);
    if (!Number.isFinite(startedAt) || !Number.isFinite(completedAt)
        || completedAt < startedAt
        || Math.abs((completedAt - startedAt) - value.duration_ms) > 1) {
        context.addIssue({ code: "custom", path: ["duration_ms"],
            message: "Author pass timestamps or duration are invalid or inconsistent." });
    }
    const usage = [value.input_tokens, value.output_tokens, value.total_tokens];
    if (usage.some((member) => member === null) !== usage.every((member) => member === null)
        || (value.total_tokens !== null
            && value.total_tokens !== (value.input_tokens ?? 0) + (value.output_tokens ?? 0))) {
        context.addIssue({ code: "custom", path: ["total_tokens"],
            message: "Author pass token usage is incomplete or inconsistent." });
    }
});
const authorPassOutcomeLedgerPathSchema = z.string().min(1).max(512).refine((value) => {
    const parts = value.split("/");
    return !value.startsWith("/") && !value.endsWith("/") && !value.includes("\\")
        && parts.every((part) => part !== "." && part !== ".."
            && /^[A-Za-z0-9._$-]+$/u.test(part));
}, "Author pass outcome ledger paths must be ASCII, canonical, and relative.");
const authorPassOutcomeEvidencePathSchema = z.string().regex(/^evidence\/(?:audits|postgres)\/[a-z][a-z0-9_]{0,126}\.json$/u, "Author pass runtime evidence must use a canonical evidence path.");
const authorPassOutcomeMemberSchema = z.object({
    path: authorPassOutcomeLedgerPathSchema,
    sha256: sha256Schema,
    bytes: z.number().int().nonnegative().max(48 * 1_024 * 1_024).safe(),
}).strict();
const authorPassOutcomeRuntimeEvidenceSummarySchema = z.object({
    path: authorPassOutcomeEvidencePathSchema,
    member_sha256: sha256Schema,
    evidence_kind: z.enum(["product_view_audit", "postgres_canonical_view"]),
    evidence: z.record(z.string(), z.unknown()),
}).strict().superRefine((value, context) => {
    const expectedPrefix = value.evidence_kind === "product_view_audit"
        ? "evidence/audits/"
        : "evidence/postgres/";
    if (!value.path.startsWith(expectedPrefix)) {
        context.addIssue({
            code: "custom",
            path: ["path"],
            message: "Author pass runtime evidence kind must match its canonical path.",
        });
    }
});
const sortedUniqueOutcomeInventory = (values, context, path) => {
    const memberPaths = values.map((value) => value.path);
    const sortedPaths = [...memberPaths].sort();
    if (new Set(memberPaths).size !== memberPaths.length
        || JSON.stringify(memberPaths) !== JSON.stringify(sortedPaths)) {
        context.addIssue({
            code: "custom",
            path: [path],
            message: "Author pass outcome inventory paths must be unique and sorted.",
        });
    }
};
export const twinAuthoringAuthorPassOutcomeV1Schema = z.object({
    artifact_kind: z.literal("twin_authoring_author_pass_outcome"),
    schema_version: z.literal("1.0.0"),
    run_id: z.string().regex(/^ta_run_[0-9a-f]{32}$/u),
    attempt_count: z.number().int().positive().safe(),
    pass_index: z.number().int().min(1).max(27),
    author_pass_artifact_sha256: sha256Schema,
    previous_outcome_artifact_sha256: sha256Schema.nullable(),
    semantic_plan_sha256: sha256Schema,
    status: z.enum(["passed", "failed"]),
    member_ledger: z.array(authorPassOutcomeMemberSchema).max(512),
    member_ledger_sha256: sha256Schema,
    row_free_runtime_evidence: z.array(authorPassOutcomeRuntimeEvidenceSummarySchema).max(512),
    row_free_runtime_evidence_sha256: sha256Schema,
    failure_history_entry: z.object({
        author_pass_index: z.number().int().min(1).max(27),
        semantic_plan_sha256: sha256Schema,
        failed_candidate_attempts: z.array(z.record(z.string(), z.unknown())).max(64),
        failures: z.array(z.record(z.string(), z.unknown())).min(1).max(64),
    }).strict().nullable(),
    row_bearing_payloads: z.literal(0),
}).strict().superRefine((value, context) => {
    if ((value.pass_index === 1) !== (value.previous_outcome_artifact_sha256 === null)) {
        context.addIssue({
            code: "custom",
            path: ["previous_outcome_artifact_sha256"],
            message: "Author pass outcome predecessor is inconsistent with its pass index.",
        });
    }
    if ((value.status === "passed") !== (value.failure_history_entry === null)) {
        context.addIssue({
            code: "custom",
            path: ["failure_history_entry"],
            message: "Only failed author pass outcomes may contain failure history.",
        });
    }
    const failure = value.failure_history_entry;
    if (failure && (failure.author_pass_index !== value.pass_index
        || failure.semantic_plan_sha256 !== value.semantic_plan_sha256)) {
        context.addIssue({
            code: "custom",
            path: ["failure_history_entry"],
            message: "Author pass outcome failure history must bind its pass and semantic plan.",
        });
    }
    sortedUniqueOutcomeInventory(value.member_ledger, context, "member_ledger");
    sortedUniqueOutcomeInventory(value.row_free_runtime_evidence, context, "row_free_runtime_evidence");
    const memberByPath = new Map(value.member_ledger.map((member) => [member.path, member.sha256]));
    if (value.row_free_runtime_evidence.some((evidence) => memberByPath.get(evidence.path) !== evidence.member_sha256)) {
        context.addIssue({
            code: "custom",
            path: ["row_free_runtime_evidence"],
            message: "Author pass runtime evidence must bind exactly one member-ledger entry.",
        });
    }
});
export const twinAuthoringRunArtifactLinkSchema = twinAuthoringScopeSchema.extend({
    runId: twinAuthoringRunRecordSchema.shape.id,
    attemptCount: z.number().int().nonnegative().safe(),
    artifactId: z.string().regex(/^ta_art_[0-9a-f]{32}$/u),
    role: twinAuthoringArtifactRoleSchema,
    createdAt: positiveTimeSchema,
}).strict();
const modelReceiptReferenceSchema = z.object({
    artifactId: z.string().regex(/^ta_art_[0-9a-f]{32}$/u),
    exchangeArtifactId: z.string().regex(/^ta_art_[0-9a-f]{32}$/u),
    receiptId: identifierSchema,
    provider: boundedText(128),
    model: boundedText(2_000),
    implementationSha256: sha256Schema,
    promptSha256: sha256Schema,
    semanticInputSha256: sha256Schema,
    semanticOutputSha256: sha256Schema,
    providerRequestBodySha256: sha256Schema,
    providerStructuredOutputSha256: sha256Schema,
}).strict();
export const twinAuthoringDecisionV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-authoring.decision.v1"),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    runId: twinAuthoringRunRecordSchema.shape.id,
    attemptCount: z.number().int().positive().safe(),
    outcome: twinAuthoringDecisionOutcomeSchema,
    decisionArtifactId: z.string().regex(/^ta_art_[0-9a-f]{32}$/u),
    semanticAuthoringArtifactId: z.string().regex(/^ta_art_[0-9a-f]{32}$/u),
    semanticEvaluationArtifactId: z.string().regex(/^ta_art_[0-9a-f]{32}$/u),
    author: modelReceiptReferenceSchema,
    evaluator: modelReceiptReferenceSchema,
    policy: z.object({ id: identifierSchema, version: boundedText(80), sha256: sha256Schema }).strict(),
    authoringBuildSha256: sha256Schema,
    sourceSnapshotSha256: sha256Schema,
    reasonCodes: z.array(z.string().regex(/^[a-z0-9][a-z0-9_.:-]{0,127}$/u)).min(1).max(32)
        .transform((codes) => [...codes].sort()),
    createdBy: authoringCreatedBySchema,
    createdAt: positiveTimeSchema,
}).strict().superRefine((decision, context) => {
    if (new Set(decision.reasonCodes).size !== decision.reasonCodes.length) {
        context.addIssue({ code: "custom", path: ["reasonCodes"], message: "Reason codes must be unique." });
    }
    if (decision.author.receiptId === decision.evaluator.receiptId) {
        context.addIssue({
            code: "custom",
            path: ["evaluator", "receiptId"],
            message: "Author and evaluator receipts must be independent.",
        });
    }
});
const releaseArtifactReferenceSchema = z.object({
    artifactId: z.string().regex(/^ta_art_[0-9a-f]{32}$/u),
    kind: twinAuthoringArtifactKindSchema,
    contentSha256: sha256Schema,
}).strict();
export const twinAuthoringReleaseV1Schema = z.object({
    schemaVersion: z.literal("mlx.twin-authoring.release.v1"),
    tenantSlug: authoringTenantSlugSchema,
    organisationId: organisationIdSchema,
    runId: twinAuthoringRunRecordSchema.shape.id,
    attemptCount: z.number().int().positive().safe(),
    acceptedDecisionId: z.string().regex(/^ta_dec_[0-9a-f]{32}$/u),
    acceptedDecisionSha256: sha256Schema,
    verifiedReleaseArtifactId: z.string().regex(/^ta_art_[0-9a-f]{32}$/u),
    verifiedReleaseArtifactSha256: sha256Schema,
    closureSha256: sha256Schema,
    artifacts: z.array(releaseArtifactReferenceSchema).length(18)
        .transform((artifacts) => [...artifacts].sort((left, right) => left.artifactId < right.artifactId
        ? -1 : left.artifactId > right.artifactId ? 1 : 0)),
    createdBy: authoringCreatedBySchema,
    createdAt: positiveTimeSchema,
}).strict().superRefine((release, context) => {
    const references = release.artifacts.map(({ artifactId }) => artifactId);
    if (new Set(references).size !== references.length) {
        context.addIssue({ code: "custom", path: ["artifacts"], message: "Release artifacts must be unique." });
    }
    const releaseArtifact = release.artifacts.find(({ artifactId }) => artifactId === release.verifiedReleaseArtifactId);
    if (releaseArtifact?.kind !== "verified_authoring_release"
        || releaseArtifact.contentSha256 !== release.verifiedReleaseArtifactSha256) {
        context.addIssue({
            code: "custom",
            path: ["verifiedReleaseArtifactId"],
            message: "The verified release artifact must appear with its exact content hash.",
        });
    }
});
export function canonicalTwinAuthoringRunRequestJson(input) {
    return canonicalTwinBoundaryJson(twinAuthoringRunRequestV1Schema.parse(input));
}
export function canonicalTwinAuthoringEventJson(input) {
    return canonicalTwinBoundaryJson(twinAuthoringRunEventV1Schema.parse(input));
}
export function canonicalTwinAuthoringDecisionJson(input) {
    return canonicalTwinBoundaryJson(twinAuthoringDecisionV1Schema.parse(input));
}
export function canonicalTwinAuthoringReleaseJson(input) {
    return canonicalTwinBoundaryJson(twinAuthoringReleaseV1Schema.parse(input));
}
